import Comparison from '@/types/comparison';
import { FormattedHTMLMessage } from '@/utils/i18n';
import React, { FC, ReactElement } from 'react';
import MissingItemsBody from './MissingItemsBody';
import UnallocatedPortfoliosBody from './UnallocatedPortfoliosBody';
import PlanRemovedPortfoliosBody from './PlanRemovedPortfoliosBody';

interface Props {
  isMissingAllItems?: boolean;
  hasUnallocatedPortfolios: boolean;
  hasPlanRemovedPortfolios: boolean;
  allItemsAreUnallocatedPortfolios?: boolean;
  isAllMissingOrUnallocatedPortfolios?: boolean;
  unallocatedPortfolios?: Comparison['modifiedItems'];
  removedPlanPortfolios?: Comparison['modifiedItems'];
  comparison: Comparison;
}

const ModalBody: FC<Props> = ({
  comparison,
  isAllMissingOrUnallocatedPortfolios,
  isMissingAllItems,
  hasUnallocatedPortfolios,
  hasPlanRemovedPortfolios,
  unallocatedPortfolios = [],
  removedPlanPortfolios = [],
  allItemsAreUnallocatedPortfolios,
}): ReactElement => {
  const hasMissingItems = (comparison.missingItems && comparison.missingItems?.length > 0) ?? false;

  const getBottomText = (hasMissingItems: boolean, hasUnallocatedPortfolios: boolean, hasPlanRemovedPortfolios: boolean): ReactElement | null => {
    if (isAllMissingOrUnallocatedPortfolios || isMissingAllItems || allItemsAreUnallocatedPortfolios) {
      return renderBottomText('ComparisonMissingItemsModal.Body.MissingAllItems.Bottom', {
        count: comparison.missingItems?.length ?? 0 + unallocatedPortfolios.length,
        comparison: comparison.name,
      });
    }

    let removedItemsMessaging;

    if (hasMissingItems && hasUnallocatedPortfolios) {
      removedItemsMessaging = renderBottomText('ComparisonMissingItemsModal.Body.MissingSomeItemsAndUnallocatedPortfolios.Bottom', {
        count: comparison.missingItems?.length ?? 0 + unallocatedPortfolios.length,
        comparison: comparison.name,
      });
    }

    if (hasMissingItems) {
      removedItemsMessaging = renderBottomText('ComparisonMissingItemsModal.Body.MissingSomeItems.Bottom', {
        count: comparison.missingItems?.length,
        comparison: comparison.name,
      });
    }

    if (hasUnallocatedPortfolios) {
      removedItemsMessaging = renderBottomText('ComparisonMissingItemsModal.Body.SomeUnallocatedPortfolios.Bottom', {
        count: unallocatedPortfolios.length,
        comparison: comparison.name,
      });
    }

    let keepingItemsMessaging;

    if (hasPlanRemovedPortfolios) {
      keepingItemsMessaging = renderBottomText('ComparisonMissingItemsModal.Body.UnlinkedPlanAccount.Bottom', {
        count: removedPlanPortfolios.length,
        comparison: comparison.name,
      });
    }

    if (!removedItemsMessaging && !keepingItemsMessaging) {
      return null;
    }

    return (
      <>
        {removedItemsMessaging}
        {keepingItemsMessaging}
      </>
    );
  };

  const renderBottomText = (id: string, values: Record<string, any>) => (
    <p>
      <FormattedHTMLMessage id={id} values={values} />
    </p>
  );

  if (isAllMissingOrUnallocatedPortfolios) {
    return (
      <>
        <p>
          <FormattedHTMLMessage id="ComparisonMissingItemsModal.Body.Common.Top" />
        </p>
        <MissingItemsBody
          comparison={comparison}
          showTop={false}
          isMissingAllItems={isMissingAllItems}
          isAllMissingOrUnallocatedPortfolios={isAllMissingOrUnallocatedPortfolios}
        />
        <UnallocatedPortfoliosBody comparison={comparison} unallocatedPortfolios={unallocatedPortfolios} showTop={false} />
        {getBottomText(hasMissingItems, hasUnallocatedPortfolios, hasPlanRemovedPortfolios)}
      </>
    );
  }
  if (hasMissingItems || hasUnallocatedPortfolios || hasPlanRemovedPortfolios) {
    return (
      <>
        {hasMissingItems && (
          <MissingItemsBody
            comparison={comparison}
            isMissingAllItems={isMissingAllItems}
            isAllMissingOrUnallocatedPortfolios={isAllMissingOrUnallocatedPortfolios}
          />
        )}
        {hasUnallocatedPortfolios && <UnallocatedPortfoliosBody comparison={comparison} unallocatedPortfolios={unallocatedPortfolios} />}
        {hasPlanRemovedPortfolios && <PlanRemovedPortfoliosBody comparison={comparison} removedPlanPortfolios={removedPlanPortfolios} />}
        {getBottomText(hasMissingItems, hasUnallocatedPortfolios, hasPlanRemovedPortfolios)}
      </>
    );
  }
  return <></>;
};

export default ModalBody;
